import { useDispatch, useSelector } from 'react-redux';

import { authentication } from '@perpay-web/authentication/singleton';
import { getIsLoadingOrUnrequested } from '@perpay-web/data-module/useDataModule';
import {
    analyticsPartnerHostedOnboardingClickLearnMore,
    analyticsPartnerHostedOnboardingCloseDDModal,
    analyticsPartnerHostedOnboardingViewDDModal,
} from '@perpay-web/fintech/actions/analytics/partnerHostedOnboarding';
import { analyticsPerpayCreditCardOnboardingReviewDirectDepositStepViewed } from '@perpay-web/fintech/actions/analytics/perpayCreditCardOnboarding';
import {
    createDeduction,
    clearDeductionError,
} from '@perpay-web/fintech/actions/entities/deductions';
import { payrollPaymentSetupStart } from '@perpay-web/fintech/actions/payrollPaymentSetup';
import { replaceAmountToCharge } from '@perpay-web/fintech/actions/ui/cardOnboardingMVP2';
import CardApplicationDirectDepositSummary from '@perpay-web/fintech/components/composite/CardApplicationDirectDepositSummary/CardApplicationDirectDepositSummary';
import {
    MANUAL_PAYMENT_SETUP_STEP,
    REVIEW_DIRECT_DEPOSIT_STEP,
    SELECT_MINIMUM_PAYMENT_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useCardApplicationContext } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import {
    cardPaymentSummaryDataModule,
    getIsPayrollPaymentSetupUser,
    getIsPinwheelEligible,
} from '@perpay-web/fintech/dataModules/composed/cardPaymentSummary';
import {
    getCardMinimumPayment,
    getCoreMinimumPayment,
    getMinimumPayment,
    getPayCycleType,
} from '@perpay-web/fintech/dataModules/generalAccountSummary';
import { createPayrollDeductionSetAmount } from '@perpay-web/fintech/hooks/dataModule/useCreatePayrollDeductionContext';
import {
    getCardApplicationCreditLimit,
    useFetchCardApplicationStatusContext,
} from '@perpay-web/fintech/hooks/dataModule/useFetchCardApplicationStatusContext';

const CardApplicationDirectDepositSummaryContainer = ({
    className,
    applicationUuid,
    welcomeOffer,
}) => {
    const dispatch = useDispatch();
    const isPaymentSummaryLoading = useSelector(
        cardPaymentSummaryDataModule.getIsLoadingOrUnrequested,
    );
    const totalMinimumPayment = useSelector(getMinimumPayment);
    const coreMinimumPayment = useSelector(getCoreMinimumPayment);
    const cardMinimumPayment = useSelector(getCardMinimumPayment);
    const payCycleType = useSelector(getPayCycleType);
    const isMPUser = coreMinimumPayment > 0;
    const isPinwheelEligible = useSelector(getIsPinwheelEligible);
    const { setStep } = useCardApplicationContext();
    const { dataRequest, state } = useFetchCardApplicationStatusContext();
    const isCardApplicationLoading = getIsLoadingOrUnrequested(state);
    const isLoading = isPaymentSummaryLoading || isCardApplicationLoading;

    const creditLimit = getCardApplicationCreditLimit(state);
    const partnerCode = authentication.getPartnerOnboardedCode();
    const isPayrollPaymentSetupUser = useSelector(getIsPayrollPaymentSetupUser);

    return (
        <CardApplicationDirectDepositSummary
            className={className}
            partnerCode={partnerCode}
            welcomeOffer={welcomeOffer}
            onMount={() => {
                if (applicationUuid && !creditLimit) {
                    dataRequest(applicationUuid);
                }
                dispatch(cardPaymentSummaryDataModule.dataRequest());
                dispatch(
                    analyticsPerpayCreditCardOnboardingReviewDirectDepositStepViewed(
                        REVIEW_DIRECT_DEPOSIT_STEP,
                        partnerCode,
                    ),
                );
            }}
            onUnmount={() => {
                dispatch(cardPaymentSummaryDataModule.dataReset());
                dispatch(clearDeductionError());
            }}
            isLoading={isLoading}
            isMPUser={isMPUser}
            totalMinimumPayment={totalMinimumPayment}
            coreMinimumPayment={coreMinimumPayment}
            cardMinimumPayment={cardMinimumPayment}
            payCycleType={payCycleType}
            creditLimit={creditLimit}
            onClickLearnMore={() => {
                dispatch(analyticsPartnerHostedOnboardingClickLearnMore());
            }}
            onDirectDepositModalViewed={() => {
                dispatch(analyticsPartnerHostedOnboardingViewDDModal());
            }}
            onDirectDepositModalClosed={() => {
                dispatch(analyticsPartnerHostedOnboardingCloseDDModal());
            }}
            onClickContinue={() => {
                setStep(SELECT_MINIMUM_PAYMENT_STEP);

                if (isPayrollPaymentSetupUser) {
                    dispatch(replaceAmountToCharge(totalMinimumPayment));
                    dispatch(
                        createPayrollDeductionSetAmount(totalMinimumPayment),
                    );
                    dispatch(payrollPaymentSetupStart());
                    return;
                }

                if (isPinwheelEligible) {
                    dispatch(
                        createDeduction({
                            amount: totalMinimumPayment,
                            isPerpaySplit: false,
                        }),
                    );
                }

                dispatch(replaceAmountToCharge(totalMinimumPayment));

                if (!isPinwheelEligible) {
                    setStep(MANUAL_PAYMENT_SETUP_STEP);
                }
            }}
        />
    );
};

export default CardApplicationDirectDepositSummaryContainer;
